// lib
import { batch } from "react-redux";
import queryString from "query-string";
import Moment from "moment";
import { extendMoment } from "moment-range";
// src
import back from "services/plannings";
import { daysInRange } from "utils/dates";
import { getPresetId, getRequestId, isPermanentId, isInstantId } from "utils/presets";
import { FETCHABLE_RESOURCES } from "config/constants";
import { peopleSuccess } from "actions/resources";
import * as sync from "./index";
import { destroyPreset, qualifyPreset } from "../presets";

const moment = extendMoment(Moment);

// Private helpers
const fetchDependencies = (dispatch, getState, preset) => {
  if (preset.params) {
    FETCHABLE_RESOURCES.forEach(({ plural, idsLabel, fetch }) => {
      const resources = preset.params[plural];
      if (resources) {
        const resourceIds = getState().resources[idsLabel];
        const missings = resources.filter((id) => !resourceIds.includes(id));
        if (missings.length !== 0) {
          dispatch(fetch(missings));
        }
      }
    });
  }
};

const requestEvents = (dispatch, getState, call, computeDisplay) => {
  const { endpoint, query, expand, permanent } = call;
  const { dateStart, dateEnd } = getState().table;
  const fullQuery = expand ? { ...query, expand: true, withTitle: true } : query;

  const flatQuery = queryString.stringify(fullQuery, { arrayFormat: "bracket" });

  return back.request(
    `${endpoint}?${flatQuery}`,
    dispatch,
    getState,
    () => dispatch(sync.eventsRequest(query)),
    (json) => {
      batch(() => {
        dispatch(sync.eventsSuccess(json));
        // Enrich presets#byId with additional info about the preset,
        // since only its id is known before the fetch
        const { meta } = json;
        const { resources, title, titleLink } = meta;
        let preset = { id: getPresetId(query), name: title, link: titleLink };
        if (permanent) {
          const { params, options } = meta;
          preset = { ...preset, params, options };
        }
        dispatch(qualifyPreset(preset));
        // Enrich resources
        if (resources) {
          FETCHABLE_RESOURCES.forEach(({ plural, success }) => {
            if (resources[plural]) dispatch(success(resources[plural]));
          });
          // Enrich specific people resources
          if (resources.people) dispatch(peopleSuccess(resources.people));
        }
        const { isLoadingInRange } = getState().table;
        if (!isLoadingInRange) {
          dispatch(computeDisplay());
          dispatch(sync.cacheEvents({ dateStart, dateEnd }));
        }
      });
    },
    (status) => {
      batch(() => {
        if ([404, 406].includes(status)) {
          dispatch(destroyPreset(query));
        }
        dispatch(sync.eventsFailure(query));
      });
    },
  );
};

const isQueryInQueue = (getState, query) => {
  const { queue } = getState().table;
  const requestId = getRequestId(query);
  return Boolean(queue.find(({ requestId: id }) => id === requestId));
};

const shortenRange = (wishedDays, cachedDays) => {
  const range = [];
  wishedDays.forEach((day) => {
    if (!cachedDays.includes(day)) range.push(day);
  });
  if (range.length === 0) return range;
  // one day missing
  if (range.length === 1) range.push(range[0]);

  const [first] = range;
  const last = range[range.length - 1];
  return [moment(first).startOf("day").valueOf(), moment(last).endOf("day").valueOf()];
};

const shortenFromCache = (getState, query) => {
  const { dateStart, dateEnd } = query;
  const id = getPresetId(query);
  const { needsRefresh, byPreset } = getState().events;
  // don't shorten when refresh needed!
  if (needsRefresh) return [dateStart, dateEnd];

  const byDay = byPreset[id] ? byPreset[id].byDay : null;

  if (byDay) {
    const now = Date.now();
    const freshDaysInCache = Object.entries(byDay)
      .filter(([, { updatedAt }]) => now - updatedAt < sync.CACHE_DELAY)
      .map(([day]) => day);
    return shortenRange(daysInRange(dateStart, dateEnd), freshDaysInCache);
  }

  // nothing in byDay cache, not shortened
  return [dateStart, dateEnd];
};

const buildCall = ({ id, params }, ds, de) => {
  const dateStart = moment(ds).toISOString();
  const dateEnd = moment(de).toISOString();

  if (isPermanentId(id))
    // expands depends from the fact preset is enabled from URL (params not known)
    // or from populars (params already loaded)
    return {
      endpoint: "/events/presets",
      query: { id, dateStart, dateEnd },
      expand: !params,
      permanent: true,
    };

  // packed preset
  if (isInstantId(id)) {
    return {
      endpoint: "/events/pack",
      query: { ...params, id, dateStart, dateEnd },
    };
  }
  // resource preset
  return {
    endpoint: "/events/resources",
    query: { ...params, dateStart, dateEnd },
    expand: true,
  };
};

// API

export const FetchType = {
  SYNC: 1,
  ASYNC: 2,
};

export const managedFetchEvents = (
  dispatch,
  getState,
  preset,
  dateStart,
  dateEnd,
  computeDisplay,
) => {
  const { query } = buildCall(preset, dateStart, dateEnd);
  // Skip but ASYNC
  const skipIfInQueue = isQueryInQueue(getState, query);
  if (skipIfInQueue) return FetchType.ASYNC;
  // Shorten range and possibly succeeds SYNChronously (from cache)
  const shortenedRange = shortenFromCache(getState, query);
  if (shortenedRange.length === 0) {
    return FetchType.SYNC;
  }
  // Fallback ASYNC path
  const [shortenedStart, shortenedEnd] = shortenedRange;
  const call = buildCall(preset, shortenedStart, shortenedEnd);

  const { expand } = call;
  if (!expand) fetchDependencies(dispatch, getState, preset);
  requestEvents(dispatch, getState, call, computeDisplay);
  return FetchType.ASYNC;
};
